import React, { useState, useEffect, useCallback } from "react";
import {
  Autocomplete,
  ButtonGroup,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Checkbox,
  IconButton,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
} from "@mui/material";
import {
  IoAdd,
  IoRefresh,
  IoTrash,
  IoLayersOutline,
  IoCloseCircle,
} from "react-icons/io5";
import { IoLockClosed, IoEyeOff, IoCheckmarkCircle } from "react-icons/io5";

import VuiBox from "components/ui/VuiBox";
import VuiTypography from "components/ui/VuiTypography";
import VuiButton from "components/ui/VuiButton";
import VuiInput from "components/ui/VuiInput";
import VuiAlert from "components/ui/VuiAlert";
import VuiBadge from "components/ui/VuiBadge";
import VuiAvatar from "components/ui/VuiAvatar";
import PorterTable from "components/PorterTable";

import { adminService } from "services/adminService";
import { useSnackbar } from "notistack";
import defaultAvatar from "assets/images/profile-1.png";

// ─── Constants ────────────────────────────────────────────────────────────────

const ROLE_PRIORITY  = ["ADMIN", "USER"];
// CLIENT role removed — it has no distinct behaviour from USER
const AVAILABLE_ROLES = ["USER", "ADMIN"];

const ALL_PAGES = [
  { key: "press-digest-v2",     label: "Press Digest" },
  { key: "digital-presence-v2", label: "LinkedIn Watch" },
  { key: "content-strategy",    label: "Content Strategy" },
  { key: "product-trends",      label: "Product Trends" },
  { key: "expansion-trends",    label: "Expansion Trends" },
  { key: "product-showdown",    label: "Product Showdown" },
  { key: "pricing-analytics",   label: "Pricing Analytics" },
  { key: "rd-landscape",        label: "R&D Landscape" },
  { key: "competitor-reports",  label: "Competitor Reports" },
  { key: "kol-insights",        label: "KOL Insights" },
];

const PAGE_HIDDEN  = "hidden";
const PAGE_LOCKED  = "locked";
const PAGE_ENABLED = "enabled";

const STATE_CONFIG = {
  [PAGE_HIDDEN]:  { label: "Hidden",  icon: <IoEyeOff size={12} />,          color: "#7B809A" },
  [PAGE_LOCKED]:  { label: "Locked",  icon: <IoLockClosed size={12} />,      color: "#fb8c00" },
  [PAGE_ENABLED]: { label: "Enabled", icon: <IoCheckmarkCircle size={12} />, color: "#17c1e8" },
};

const EMPTY_FORM = {
  username: "",
  email: "",
  password: "",
  firstName: "",
  lastName: "",
  companyName: "",
  companyId: "",
  roles: ["USER"],
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function buildPageStateMap(enabledPages, lockedPages) {
  const map = {};
  ALL_PAGES.forEach(({ key }) => {
    if (enabledPages?.includes(key))      map[key] = PAGE_ENABLED;
    else if (lockedPages?.includes(key))  map[key] = PAGE_LOCKED;
    else                                  map[key] = PAGE_HIDDEN;
  });
  return map;
}

function pageStateMapToLists(stateMap) {
  const enabledPages = [], lockedPages = [];
  ALL_PAGES.forEach(({ key }) => {
    if (stateMap[key] === PAGE_ENABLED) enabledPages.push(key);
    else if (stateMap[key] === PAGE_LOCKED) lockedPages.push(key);
  });
  return { enabledPages, lockedPages };
}

// ─── PageStateSelector (reused from ClientManagement) ────────────────────────

function PageStateSelector({ pageKey, label, value, onChange }) {
  return (
    <VuiBox
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      px={1.5}
      py={0.75}
      sx={{
        borderRadius: "8px",
        background: "rgba(255,255,255,0.03)",
        border: "1px solid rgba(255,255,255,0.06)",
      }}
    >
      <VuiTypography variant="caption" color="text" sx={{ minWidth: 130 }}>
        {label}
      </VuiTypography>
      <ButtonGroup size="small" disableElevation>
        {[PAGE_HIDDEN, PAGE_LOCKED, PAGE_ENABLED].map(state => {
          const { label: stateLabel, icon, color } = STATE_CONFIG[state];
          const active = value === state;
          return (
            <Button
              key={state}
              onClick={() => onChange(pageKey, state)}
              startIcon={icon}
              sx={{
                fontSize: "11px",
                textTransform: "none",
                px: 1,
                py: 0.25,
                borderColor: "rgba(255,255,255,0.15) !important",
                color: active ? color : "rgba(255,255,255,0.3)",
                background: active ? `${color}22` : "transparent",
                fontWeight: active ? "bold" : "normal",
                "&:hover": { background: `${color}18`, color },
              }}
            >
              {stateLabel}
            </Button>
          );
        })}
      </ButtonGroup>
    </VuiBox>
  );
}

// ─── Page Override Dialog ─────────────────────────────────────────────────────

function PageOverrideDialog({ open, user, onClose, onSaved }) {
  const { enqueueSnackbar } = useSnackbar();
  const [saving, setSaving] = useState(false);
  // null = use client config (no override); object = has override
  const [hasOverride, setHasOverride] = useState(false);
  const [pageState, setPageState] = useState({});

  useEffect(() => {
    if (!open || !user) return;
    const userHasOverride = user.enabledPages != null || user.lockedPages != null;
    setHasOverride(userHasOverride);
    setPageState(buildPageStateMap(user.enabledPages, user.lockedPages));
  }, [open, user]);

  const handlePageChange = (key, state) => {
    setPageState(s => ({ ...s, [key]: state }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { enabledPages, lockedPages } = pageStateMapToLists(pageState);
      await adminService.updateUserPageOverrides(user.id, enabledPages, lockedPages);
      enqueueSnackbar("Page overrides saved", { variant: "success" });
      onSaved();
      onClose();
    } catch {
      enqueueSnackbar("Failed to save overrides", { variant: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    setSaving(true);
    try {
      // Pass null for both to revert to client config
      await adminService.updateUserPageOverrides(user.id, null, null);
      enqueueSnackbar("Override cleared — user now follows client config", { variant: "success" });
      onSaved();
      onClose();
    } catch {
      enqueueSnackbar("Failed to clear overrides", { variant: "error" });
    } finally {
      setSaving(false);
    }
  };

  if (!user) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { background: "#0f1535", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "16px" },
      }}
    >
      <DialogTitle sx={{ color: "white", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
        <VuiBox display="flex" justifyContent="space-between" alignItems="center">
          <VuiBox>
            <VuiTypography variant="h6" color="white">Page Override</VuiTypography>
            <VuiTypography variant="caption" color="text">
              {user.firstName} {user.lastName} · {user.email}
            </VuiTypography>
          </VuiBox>
          {hasOverride && (
            <VuiBadge
              variant="standard"
              badgeContent="Override active"
              size="xs"
              container
              sx={{ border: "1px solid #fb8c00", color: "white" }}
            />
          )}
        </VuiBox>
      </DialogTitle>

      <DialogContent sx={{ pt: 2.5 }}>
        <VuiTypography variant="caption" color="text" sx={{ display: "block", mb: 2 }}>
          Overrides apply to this user only. Leave at client defaults unless there's a specific
          reason to differ. Clear the override to revert to the client config.
        </VuiTypography>
        <VuiTypography variant="caption" color="text" sx={{ display: "block", mb: 1.5 }}>
          <strong style={{ color: "#17c1e8" }}>Enabled</strong> — accessible ·{" "}
          <strong style={{ color: "#fb8c00" }}>Locked</strong> — visible, upsell prompt ·{" "}
          <strong style={{ color: "#7B809A" }}>Hidden</strong> — not shown
        </VuiTypography>
        <VuiBox display="flex" flexDirection="column" gap={0.75}>
          {ALL_PAGES.map(page => (
            <PageStateSelector
              key={page.key}
              pageKey={page.key}
              label={page.label}
              value={pageState[page.key] || PAGE_HIDDEN}
              onChange={handlePageChange}
            />
          ))}
        </VuiBox>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, borderTop: "1px solid rgba(255,255,255,0.08)", gap: 1 }}>
        {hasOverride && (
          <VuiButton variant="outlined" color="warning" size="small" onClick={handleClear}
            disabled={saving} sx={{ mr: "auto" }}>
            <IoCloseCircle size={14} style={{ marginRight: 4 }} />
            Clear Override
          </VuiButton>
        )}
        <VuiButton variant="outlined" color="secondary" onClick={onClose}>Cancel</VuiButton>
        <VuiButton color="info" onClick={handleSave} disabled={saving}>
          {saving ? "Saving…" : "Save Override"}
        </VuiButton>
      </DialogActions>
    </Dialog>
  );
}

// ─── New User Dialog ──────────────────────────────────────────────────────────

function NewUserDialog({ open, onClose, onCreated }) {
  const { enqueueSnackbar } = useSnackbar();
  const [form, setForm] = useState(EMPTY_FORM);
  const [clientOrgs, setClientOrgs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState({ type: "", message: "" });

  useEffect(() => {
    if (!open) return;
    setForm(EMPTY_FORM);
    setStatus({ type: "", message: "" });
    adminService.getClientOrgs()
      .then(res => setClientOrgs(Array.isArray(res) ? res : res.data || []))
      .catch(() => enqueueSnackbar("Failed to load client organisations", { variant: "error" }));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  };

  const handleRoleChange = e => {
    const { value } = e.target;
    setForm(f => ({ ...f, roles: typeof value === "string" ? value.split(",") : value }));
  };

  const handleCompanyChange = (_, newValue) => {
    setForm(f => newValue
      ? { ...f, companyName: newValue.name, companyId: newValue.id }
      : { ...f, companyName: "", companyId: "" }
    );
  };

  const handleSubmit = async () => {
    setLoading(true);
    setStatus({ type: "", message: "" });
    try {
      await adminService.createUser(form);
      setStatus({ type: "success", message: "User created successfully!" });
      enqueueSnackbar("User created", { variant: "success" });
      onCreated();
      setTimeout(onClose, 800);
    } catch (err) {
      setStatus({ type: "error", message: err.response?.data?.message || "Failed to create user." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { background: "#0f1535", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "16px" },
      }}
    >
      <DialogTitle sx={{ color: "white", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
        <input type="text" autoComplete="off" tabIndex="-1"
          style={{ opacity: 0, position: "absolute", top: -9999, left: -9999, height: 0, width: 0 }} />
        <input type="password" autoComplete="new-password" tabIndex="-1"
          style={{ opacity: 0, position: "absolute", top: -9999, left: -9999, height: 0, width: 0 }} />
        <VuiTypography variant="h6" color="white">New User</VuiTypography>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {status.message && (
          <VuiBox mb={2}>
            <VuiAlert color={status.type === "success" ? "success" : "error"}>{status.message}</VuiAlert>
          </VuiBox>
        )}

        <VuiBox display="flex" gap={2} mb={2}>
          <VuiBox flex={1}>
            <VuiTypography variant="caption" color="white" fontWeight="bold">Username</VuiTypography>
            <VuiInput name="username" value={form.username} onChange={handleChange}
              placeholder="jdoe123" autoComplete="off" sx={{ mt: 0.5 }} />
          </VuiBox>
          <VuiBox flex={1}>
            <VuiTypography variant="caption" color="white" fontWeight="bold">Email</VuiTypography>
            <VuiInput type="email" name="email" value={form.email} onChange={handleChange}
              placeholder="jdoe@company.com" autoComplete="off" sx={{ mt: 0.5 }} />
          </VuiBox>
        </VuiBox>

        <VuiBox display="flex" gap={2} mb={2}>
          <VuiBox flex={1}>
            <VuiTypography variant="caption" color="white" fontWeight="bold">First Name</VuiTypography>
            <VuiInput name="firstName" value={form.firstName} onChange={handleChange}
              placeholder="John" autoComplete="off" sx={{ mt: 0.5 }} />
          </VuiBox>
          <VuiBox flex={1}>
            <VuiTypography variant="caption" color="white" fontWeight="bold">Last Name</VuiTypography>
            <VuiInput name="lastName" value={form.lastName} onChange={handleChange}
              placeholder="Doe" autoComplete="off" sx={{ mt: 0.5 }} />
          </VuiBox>
        </VuiBox>

        <VuiBox mb={2}>
          <VuiTypography variant="caption" color="white" fontWeight="bold">Initial Password</VuiTypography>
          <VuiInput type="password" name="password" value={form.password} onChange={handleChange}
            placeholder="Create a strong password" autoComplete="new-password" sx={{ mt: 0.5 }} />
        </VuiBox>

        <VuiBox mb={2}>
          <VuiTypography variant="caption" color="white" fontWeight="bold">Client Organisation</VuiTypography>
          <Autocomplete
            options={clientOrgs}
            getOptionLabel={o => o.name || ""}
            value={clientOrgs.find(o => o.name === form.companyName) || null}
            onChange={handleCompanyChange}
            sx={{ mt: 0.5 }}
            renderInput={params => (
              <TextField {...params} placeholder="Search client…" variant="standard"
                sx={{
                  "& .MuiInputBase-root": {
                    color: "white", padding: "4px 12px",
                    border: "1px solid #7B809A", borderRadius: "8px", background: "#0f1535",
                  },
                  "& .MuiInput-underline:before, & .MuiInput-underline:after, & .MuiInput-underline:hover:not(.Mui-disabled):before":
                    { borderBottom: "none" },
                }}
              />
            )}
            ListboxProps={{
              sx: {
                background: "#0f1535", color: "white",
                "& .MuiAutocomplete-option": {
                  "&:hover": { background: "rgba(255,255,255,0.1)" },
                  "&[aria-selected='true']": { background: "rgba(255,255,255,0.2) !important" },
                },
              },
            }}
          />
          {form.companyId && (
            <VuiTypography variant="caption" color="text"
              sx={{ display: "block", mt: 0.5, fontFamily: "monospace", fontSize: "11px" }}>
              ID: {form.companyId}
            </VuiTypography>
          )}
        </VuiBox>

        <VuiBox>
          <VuiTypography variant="caption" color="white" fontWeight="bold">Roles</VuiTypography>
          <FormControl sx={{ width: "100%", mt: 0.5 }}>
            <Select
              multiple
              value={form.roles}
              onChange={handleRoleChange}
              input={<OutlinedInput />}
              renderValue={selected => selected.join(", ")}
              sx={{
                color: "white", borderRadius: "8px",
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "grey.700" },
                "& .MuiSvgIcon-root": { color: "white" },
              }}
            >
              {AVAILABLE_ROLES.map(role => (
                <MenuItem key={role} value={role}>
                  <Checkbox checked={form.roles.includes(role)} />
                  <ListItemText primary={role} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </VuiBox>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, borderTop: "1px solid rgba(255,255,255,0.08)" }}>
        <VuiButton variant="outlined" color="secondary" onClick={onClose}>Cancel</VuiButton>
        <VuiButton color="info" onClick={handleSubmit} disabled={loading}>
          {loading ? "Creating…" : "Create User"}
        </VuiButton>
      </DialogActions>
    </Dialog>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

function UserManagement() {
  const { enqueueSnackbar } = useSnackbar();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newUserOpen, setNewUserOpen] = useState(false);
  const [overrideTarget, setOverrideTarget] = useState(null); // user object

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminService.getAllUsers();
      setUsers(Array.isArray(res) ? res : res.data || []);
    } catch {
      enqueueSnackbar("Failed to load users", { variant: "error" });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const handleStatusChange = async (userId, currentStatus) => {
    const newStatus = !currentStatus;
    setUsers(prev => prev.map(u => u.id === userId ? { ...u, enabled: newStatus } : u));
    try {
      await adminService.updateUserStatus(userId, newStatus);
      enqueueSnackbar(`User ${newStatus ? "enabled" : "disabled"}`, { variant: "success" });
    } catch {
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, enabled: !newStatus } : u));
      enqueueSnackbar("Failed to update user status", { variant: "error" });
    }
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Permanently delete ${user.email}? This cannot be undone.`)) return;
    try {
      await adminService.deleteUser(user.id);
      setUsers(prev => prev.filter(u => u.id !== user.id));
      enqueueSnackbar("User deleted", { variant: "success" });
    } catch {
      enqueueSnackbar("Failed to delete user", { variant: "error" });
    }
  };

  const columns = [
    { key: "user",    name: "User",    align: "left" },
    { key: "company", name: "Client",  align: "left" },
    { key: "role",    name: "Role",    align: "center" },
    { key: "status",  name: "Status",  align: "center" },
    { key: "enable",  name: "Enable",  align: "center" },
    { key: "actions", name: "",        align: "center" },
  ];

  const rows = users.map(user => ({
    user: (
      <VuiBox display="flex" alignItems="center" px={1} py={0.5}>
        <VuiBox mr={2}>
          <VuiAvatar src={defaultAvatar} alt={user.username} size="sm" variant="rounded" />
        </VuiBox>
        <VuiBox display="flex" flexDirection="column">
          <VuiTypography variant="button" color="white" fontWeight="medium">
            {user.firstName} {user.lastName}
          </VuiTypography>
          <VuiTypography variant="caption" color="text">{user.email}</VuiTypography>
        </VuiBox>
      </VuiBox>
    ),
    company: (
      <VuiTypography variant="caption" color="white" fontWeight="medium">
        {user.companyName || <span style={{ opacity: 0.4 }}>—</span>}
      </VuiTypography>
    ),
    role: (
      <Stack direction="row" spacing={0.5} justifyContent="center">
        {[...(user?.roles ?? [])]
          .map(r => r.name)
          .sort((a, b) => ROLE_PRIORITY.indexOf(a) - ROLE_PRIORITY.indexOf(b))
          .map(role => (
            <VuiBadge
              key={role}
              variant="standard"
              badgeContent={role}
              size="xs"
              container
              sx={({ palette: { white, success, info } }) => ({
                border: `1px solid ${role === "ADMIN" ? success.main : info.main}`,
                color: white.main,
              })}
            />
          ))}
      </Stack>
    ),
    status: (
      <VuiBadge
        variant="standard"
        badgeContent={user.enabled ? "Active" : "Disabled"}
        size="xs"
        container
        sx={({ palette: { white, success, error } }) => ({
          border: `1px solid ${user.enabled ? success.main : error.main}`,
          color: white.main,
        })}
      />
    ),
    enable: (
      <Switch
        checked={user.enabled}
        onChange={() => handleStatusChange(user.id, user.enabled)}
        sx={{
          "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
            backgroundColor: "#39d174 !important",
            borderColor: "#39d174 !important",
          },
          "& .MuiSwitch-switchBase:not(.Mui-checked) + .MuiSwitch-track": {
            backgroundColor: "#e53935 !important",
            borderColor: "#e53935 !important",
            opacity: "0.7 !important",
          },
        }}
      />
    ),
    actions: (
      <Stack direction="row" spacing={0.5} justifyContent="center">
        <Tooltip title={user.enabledPages != null || user.lockedPages != null
          ? "Page override active — click to edit"
          : "Override pages for this user"}>
          <IconButton
            size="small"
            onClick={() => setOverrideTarget(user)}
            sx={{
              color: (user.enabledPages != null || user.lockedPages != null)
                ? "#fb8c00"
                : "rgba(255,255,255,0.3)",
              "&:hover": { color: "#17c1e8" },
            }}
          >
            <IoLayersOutline size={15} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete user permanently">
          <IconButton
            size="small"
            onClick={() => handleDelete(user)}
            sx={{ color: "rgba(255,255,255,0.3)", "&:hover": { color: "#f44335" } }}
          >
            <IoTrash size={15} />
          </IconButton>
        </Tooltip>
      </Stack>
    ),
  }));

  return (
    <VuiBox>
      <VuiBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <VuiBox>
          <VuiTypography variant="lg" color="white" fontWeight="bold">Platform Users</VuiTypography>
          <VuiTypography variant="caption" color="text">
            {users.length} user{users.length !== 1 ? "s" : ""}
          </VuiTypography>
        </VuiBox>
        <VuiBox display="flex" gap={1}>
          <Tooltip title="Refresh">
            <span>
              <VuiButton variant="outlined" color="secondary" size="small" onClick={loadUsers}>
                <IoRefresh size={16} />
              </VuiButton>
            </span>
          </Tooltip>
          <VuiButton color="info" size="small" onClick={() => setNewUserOpen(true)}>
            <IoAdd size={16} style={{ marginRight: 4 }} /> New User
          </VuiButton>
        </VuiBox>
      </VuiBox>

      {loading
        ? <VuiBox display="flex" justifyContent="center" py={4}>
            <CircularProgress size={24} sx={{ color: "#1AB6FF" }} />
          </VuiBox>
        : <PorterTable columns={columns} rows={rows} />
      }

      <NewUserDialog
        open={newUserOpen}
        onClose={() => setNewUserOpen(false)}
        onCreated={loadUsers}
      />

      <PageOverrideDialog
        open={!!overrideTarget}
        user={overrideTarget}
        onClose={() => setOverrideTarget(null)}
        onSaved={loadUsers}
      />
    </VuiBox>
  );
}

export default UserManagement;
